import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { getItem, setItem, removeItem } from "@/lib/durableStorage";

const AuthCtx = createContext(null);
const USER_KEY = "auth_user_v1";

function loadCached() {
  try { const raw = getItem(USER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function saveCached(u) {
  if (u) setItem(USER_KEY, JSON.stringify(u)); else removeItem(USER_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadCached);
  const [loading, setLoading] = useState(() => !loadCached());

  useEffect(() => {
    // Boot policy: trust the cached user. NEVER auto-logout on /auth/me failure.
    // iOS PWA app-kill can wipe session cookies; a 401 here MUST NOT eject the
    // user. We only refresh the profile silently in the background.
    if (!loadCached()) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); saveCached(r.data); })
      .catch(() => { /* keep cached user; do NOT clear auth */ })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setItem("token", data.token);
    saveCached(data.user); setUser(data.user);
    try { sessionStorage.removeItem("malat_splash_seen_v1"); } catch { /* ignore */ }
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    setItem("token", data.token);
    const u = { id: data.user.id || data.user._id, email: data.user.email, name: data.user.name,
      role: data.user.role || "user", language: data.user.language || "en", currency: data.user.currency || "AED" };
    saveCached(u); setUser(u);
    try { sessionStorage.removeItem("malat_splash_seen_v1"); } catch { /* ignore */ }
    return data.user;
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {}); // clear server-set cookie
    removeItem("token"); saveCached(null); setUser(null);
    try { sessionStorage.removeItem("malat_splash_seen_v1"); } catch { /* ignore */ }
    window.location.href = "/login";
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    saveCached(data); setUser(data);
    return data;
  }, []);

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); saveCached(data); setUser(data); return data; }
    catch { return null; }
  }, []);

  const value = useMemo(() => ({ user, loading, login, signup, logout, updateProfile, refreshUser, setUser }), [user, loading, login, signup, logout, updateProfile, refreshUser]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
